"use client";

import { useForm, useFieldArray, useWatch } from "react-hook-form";
import Link from "next/link";
import {
  HiCode,
  HiDownload,
  HiClipboardCopy,
  HiPlus,
  HiTrash,
  HiQuestionMarkCircle,
} from "@/components/icons";
import { FaChrome } from "@/components/icons";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import { useToast } from "@/hooks/use-toast";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import UnifiedFAQ from "@/components/UnifiedFAQ";
import { manifestGeneratorFAQ } from "@/lib/data/faq/manifest-generator";
import { faqToFAQPageSchema } from "@/lib/schemas/faq-schemas";
import JSONLDSchema from "@/components/JSONLDSchema";

// Common permissions for V3
const COMMON_PERMISSIONS = [
  "activeTab",
  "alarms",
  "bookmarks",
  "contextMenus",
  "cookies",
  "declarativeNetRequest",
  "declarativeNetRequestWithHostAccess",
  "downloads",
  "history",
  "identity",
  "idle",
  "notifications",
  "printing",
  "scripting",
  "storage",
  "tabs",
  "unlimitedStorage",
  "webNavigation",
  "webRequest",
];

type ManifestFormValues = {
  name: string;
  version: string;
  description: string;
  author: string;

  permissions: string[];
  hostPermissions: string; // Newline separated

  hasBackground: boolean;
  backgroundScript: string;

  hasAction: boolean;
  actionTitle: string;
  actionPopup: string;

  hasOptions: boolean;
  optionsPage: string;

  contentScripts: {
    matches: string; // Newline separated
    js: string; // Newline separated
    css: string; // Newline separated
    runAt: "document_idle" | "document_start" | "document_end";
  }[];
};

type ChromeManifestV3 = {
  manifest_version: 3;
  name: string;
  version: string;
  description: string;
  author?: string;
  permissions?: string[];
  host_permissions?: string[];
  background?: {
    service_worker: string;
  };
  action?: {
    default_title?: string;
    default_popup?: string;
  };
  options_ui?: {
    page: string;
    open_in_tab: boolean;
  };
  content_scripts?: Array<{
    matches: string[];
    js?: string[];
    css?: string[];
    run_at: "document_idle" | "document_start" | "document_end";
  }>;
};

export default function ManifestGeneratorClient() {
  const { register, control } = useForm<ManifestFormValues>({
    defaultValues: {
      name: "My Awesome Extension",
      version: "1.0.0",
      description: "Does amazing things in Chrome.",
      permissions: [],
      hasBackground: false,
      backgroundScript: "background.js",
      hasAction: true,
      actionPopup: "popup.html",
      hasOptions: false,
      optionsPage: "options.html",
      contentScripts: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "contentScripts",
  });

  const values = useWatch({ control });
  const { toast } = useToast();

  const generateManifest = (): string => {
    const manifest: ChromeManifestV3 = {
      manifest_version: 3,
      name: values.name || "",
      version: values.version || "",
      description: values.description || "",
    };

    if (values.author) manifest.author = values.author;

    // Permissions
    if (values.permissions && values.permissions.length > 0) {
      manifest.permissions = values.permissions;
    }

    // Host Permissions
    if (values.hostPermissions) {
      const hosts = values.hostPermissions
        .split("\n")
        .map((h) => h.trim())
        .filter((h) => h);
      if (hosts.length > 0) manifest.host_permissions = hosts;
    }

    // Background
    if (values.hasBackground && values.backgroundScript) {
      manifest.background = {
        service_worker: values.backgroundScript,
      };
    }

    // Action
    if (values.hasAction) {
      manifest.action = {};
      if (values.actionTitle) manifest.action.default_title = values.actionTitle;
      if (values.actionPopup) manifest.action.default_popup = values.actionPopup;
    }

    // Options
    if (values.hasOptions && values.optionsPage) {
      manifest.options_ui = {
        page: values.optionsPage,
        open_in_tab: true, // Standard practice
      };
    }

    // Content Scripts
    if (values.contentScripts && values.contentScripts.length > 0) {
      manifest.content_scripts = values.contentScripts
        .filter((script) => script.matches && script.matches.trim().length > 0)
        .map((script) => {
          const contentScript: {
            matches: string[];
            js?: string[];
            css?: string[];
            run_at: "document_idle" | "document_start" | "document_end";
          } = {
            matches: (script.matches || "")
              .split("\n")
              .map((s) => s.trim())
              .filter((s) => s),
            run_at: script.runAt || "document_idle",
          };

          if (script.js) {
            const jsFiles = script.js
              .split("\n")
              .map((s) => s.trim())
              .filter((s) => s);
            if (jsFiles.length > 0) {
              contentScript.js = jsFiles;
            }
          }

          if (script.css) {
            const cssFiles = script.css
              .split("\n")
              .map((s) => s.trim())
              .filter((s) => s);
            if (cssFiles.length > 0) {
              contentScript.css = cssFiles;
            }
          }

          return contentScript;
        });
    }

    return JSON.stringify(manifest, null, 2);
  };

  const manifestJson = generateManifest();

  const copyToClipboard = () => {
    navigator.clipboard.writeText(manifestJson);
    toast({
      title: "Copied to clipboard",
      description: "Manifest JSON copied to clipboard.",
    });
  };

  const downloadManifest = () => {
    const blob = new Blob([manifestJson], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "manifest.json";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container px-4 py-12 mx-auto max-w-6xl">
      <ToolPageSchema
        name="Chrome Extension Manifest V3 Generator"
        description="Generate a compliant Chrome Extension Manifest V3 file instantly. Configure permissions, background service workers, action popups, content scripts, and more."
        url="https://plugmonkey.xyz/tool/manifest-generator/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "Manifest V3 Generator",
            url: "https://plugmonkey.xyz/tool/manifest-generator/",
          },
        ]}
      />
      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-blue-600/10 rounded-full text-blue-600">
          <FaChrome className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">Manifest V3 Generator</h1>
          <p className="text-muted-foreground mt-1">
            Generate a compliant `manifest.json` file for your Chrome Extension in seconds.
          </p>
        </div>
      </div>

      <div className="mb-8 text-muted-foreground leading-relaxed space-y-3 max-w-3xl">
        <p>
          Building a Chrome extension? You need a manifest.json file that tells Chrome what your
          extension does, what permissions it needs, and how it should behave. Writing this file
          manually is error-prone and time-consuming—one typo can break your entire extension.
        </p>
        <p>
          This tool generates a compliant Manifest V3 file instantly. Configure permissions, host
          permissions, background service workers, action popups, content scripts, and more through
          a visual interface. See your manifest.json update in real-time as you make changes, then
          download it ready to use.
        </p>
        <p>
          Perfect for developers who want to ship faster. No more copying examples, no manual JSON
          editing, just configure and download. Your manifest is ready in seconds.
        </p>
      </div>

      {/* Visually hidden h2: the settings accordion renders its triggers inside
          h3 elements, which would otherwise follow the h1 directly. */}
      <h2 className="sr-only">Manifest settings</h2>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mg-name">Extension Name</Label>
                <Input
                  id="mg-name"
                  autoComplete="off"
                  {...register("name")}
                  placeholder="My Extension"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="mg-version">Version</Label>
                  <Input id="mg-version" {...register("version")} placeholder="1.0.0" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mg-author">Author (Optional)</Label>
                  <Input id="mg-author" {...register("author")} placeholder="Your Name" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="mg-description">Description</Label>
                <Textarea
                  id="mg-description"
                  {...register("description")}
                  placeholder="What does your extension do?"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Permissions</CardTitle>
              <CardDescription>Select capabilities your extension needs.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className="grid grid-cols-2 sm:grid-cols-3 gap-3"
                role="group"
                aria-label="Extension permissions"
              >
                {COMMON_PERMISSIONS.map((perm) => (
                  <label key={perm} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      value={perm}
                      {...register("permissions")}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span>{perm}</span>
                  </label>
                ))}
              </div>

              <div className="space-y-2 pt-4">
                <Label htmlFor="mg-hostPermissions">Host Permissions (One per line)</Label>
                <Textarea
                  id="mg-hostPermissions"
                  {...register("hostPermissions")}
                  placeholder="https://*.google.com/*&#10;*://*/*"
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Required for <code>scripting</code> or accessing page content.
                </p>
              </div>
            </CardContent>
          </Card>

          <Accordion type="single" collapsible className="w-full space-y-4">
            <AccordionItem value="background" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline py-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    {...register("hasBackground")}
                    onClick={(e) => e.stopPropagation()}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span>Background Service Worker</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-2 pb-4 space-y-2">
                <Label htmlFor="mg-backgroundScript">Service Worker File Path</Label>
                <Input
                  id="mg-backgroundScript"
                  {...register("backgroundScript")}
                  placeholder="background.js"
                  disabled={!values.hasBackground}
                />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="action" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline py-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    {...register("hasAction")}
                    onClick={(e) => e.stopPropagation()}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span>Action (Popup/Icon)</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-2 pb-4 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="mg-actionPopup">Default Popup Path</Label>
                  <Input
                    id="mg-actionPopup"
                    {...register("actionPopup")}
                    placeholder="popup.html"
                    disabled={!values.hasAction}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mg-actionTitle">Default Title (Tooltip)</Label>
                  <Input
                    id="mg-actionTitle"
                    {...register("actionTitle")}
                    placeholder="Click to open"
                    disabled={!values.hasAction}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="options" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline py-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    {...register("hasOptions")}
                    onClick={(e) => e.stopPropagation()}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span>Options Page</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-2 pb-4 space-y-2">
                <Label htmlFor="mg-optionsPage">Options Page Path</Label>
                <Input
                  id="mg-optionsPage"
                  {...register("optionsPage")}
                  placeholder="options.html"
                  disabled={!values.hasOptions}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Content Scripts</CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  append({
                    matches: "*://*.example.com/*",
                    js: "content.js",
                    css: "",
                    runAt: "document_idle",
                  })
                }
              >
                <HiPlus className="mr-2" /> Add Script
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="p-4 border rounded-lg space-y-4 relative bg-muted/20"
                >
                  <Button
                    size="icon"
                    variant="ghost"
                    className="absolute top-2 right-2 text-red-500 hover:text-red-700 hover:bg-red-100"
                    onClick={() => remove(index)}
                  >
                    <HiTrash />
                  </Button>

                  <div className="space-y-2">
                    <Label htmlFor={`mg-cs-${index}-matches`}>Matches (One per line)</Label>
                    <Textarea
                      id={`mg-cs-${index}-matches`}
                      {...register(`contentScripts.${index}.matches`)}
                      placeholder="https://*.google.com/*"
                      className="font-mono text-xs h-20"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`mg-cs-${index}-js`}>JS Files (One per line)</Label>
                      <Textarea
                        id={`mg-cs-${index}-js`}
                        {...register(`contentScripts.${index}.js`)}
                        placeholder="content.js"
                        className="font-mono text-xs h-20"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`mg-cs-${index}-css`}>CSS Files (One per line)</Label>
                      <Textarea
                        id={`mg-cs-${index}-css`}
                        {...register(`contentScripts.${index}.css`)}
                        placeholder="styles.css"
                        className="font-mono text-xs h-20"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`mg-cs-${index}-runAt`}>Run At</Label>
                    <select
                      id={`mg-cs-${index}-runAt`}
                      {...register(`contentScripts.${index}.runAt`)}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      <option value="document_idle">Document Idle (Default)</option>
                      <option value="document_start">Document Start</option>
                      <option value="document_end">Document End</option>
                    </select>
                  </div>
                </div>
              ))}
              {fields.length === 0 && (
                <div className="text-center py-6 text-muted-foreground text-sm border-2 border-dashed rounded-lg">
                  No content scripts added.
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="xl:col-span-1">
          <div className="sticky top-6 space-y-6">
            <Card className="bg-slate-950 text-slate-50 border-slate-800 shadow-xl overflow-hidden">
              <CardHeader className="bg-slate-900 border-b border-slate-800 flex flex-row items-center justify-between">
                <CardTitle className="text-slate-50 font-mono text-sm flex items-center gap-2">
                  <HiCode className="text-blue-400" /> manifest.json
                </CardTitle>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={copyToClipboard}
                    className="text-slate-400 hover:text-white hover:bg-slate-800 h-8 px-2"
                  >
                    <HiClipboardCopy />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <pre className="p-4 overflow-x-auto text-xs font-mono leading-relaxed text-blue-100/90 max-h-[80vh] overflow-y-auto custom-scrollbar">
                  {manifestJson}
                </pre>
              </CardContent>
              <div className="p-4 bg-slate-900 border-t border-slate-800">
                <Button
                  onClick={downloadManifest}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold"
                >
                  <HiDownload className="mr-2 h-4 w-4" /> Download manifest.json
                </Button>
              </div>
            </Card>

            <div className="bg-card border rounded-xl p-6">
              <h3 className="font-bold text-lg mb-4">Need Icons Too?</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Once you have your manifest, you'll need properly sized icons. Use our{" "}
                <strong>Icon Resizer</strong> for Chrome extensions or{" "}
                <strong>Favicon Generator</strong> for web apps and PWAs.
              </p>
              <div className="flex flex-col gap-2">
                <Button asChild variant="secondary" className="w-full">
                  <Link href="/tool/chrome-extension-icon-resizer/">Chrome Extension Icons</Link>
                </Button>
                <Button asChild variant="secondary" className="w-full">
                  <Link href="/tool/favicon-multi-format-generator/">Web App Favicons</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <section className="mt-16 pt-12 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12 shadow-sm">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <HiQuestionMarkCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm">
                  Click on a question to view the answer.
                </p>
              </div>
            </div>
            <UnifiedFAQ
              items={manifestGeneratorFAQ.map((item) => ({
                question: item.question,
                answer: item.answer,
              }))}
              showSupportLink={false}
              className="space-y-4"
            />
          </div>
        </div>
      </section>

      {/* FAQ Schema */}
      {(() => {
        const faqSchema = faqToFAQPageSchema(manifestGeneratorFAQ, {
          url: "https://plugmonkey.xyz/tool/manifest-generator/",
          name: "Chrome Extension Manifest V3 Generator - Frequently Asked Questions",
          description:
            "Get answers to common questions about generating Chrome Extension Manifest V3 files.",
        });
        return faqSchema ? <JSONLDSchema schema={faqSchema} /> : null;
      })()}

      <ToolsCustomDevCTA />
    </div>
  );
}
